import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { getCurrentUser, User } from '../services/authService';
import { adminService } from '../services/adminService';
import { userDeletionService } from '../services/userDeletionService';
import TopBar from '../components/TopBar';
import { APP_NAME } from '../constants';

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 16px 12px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 16px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.div<{ imageUrl?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #333;
`;

const UserEmail = styled.div`
  color: #666;
  font-size: 12px;
`;

const StatusBadge = styled.span<{ variant: 'danger' | 'warning' | 'success' }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    switch (props.variant) {
      case 'danger': return '#ffebee';
      case 'warning': return '#fff3e0';
      case 'success': return '#e8f5e8';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'danger': return '#d32f2f';
      case 'warning': return '#f57c00';
      case 'success': return '#2e7d32';
      default: return '#666';
    }
  }};
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatItem = styled.div`
  font-size: 12px;
  color: #666;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #ccc;
`;

const DeletedUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [deletedUsers, setDeletedUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentUserData = await getCurrentUser();
        
        if (!currentUserData || !adminService.isAdmin(currentUserData)) {
          navigate('/dashboard');
          return;
        }

        setCurrentUser(currentUserData);

        // Fetch deleted users
        const deletedUsersData = await userDeletionService.getDeletedUsers();
        setDeletedUsers(deletedUsersData);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load deleted users data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getTotalDeletedData = (stats: any) => {
    if (!stats) return 0;
    return Object.values(stats).reduce((total: number, count: any) => total + (count || 0), 0);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading deleted users...</LoadingContainer>
      </Container>
    );
  }

  if (!currentUser || !adminService.isAdmin(currentUser)) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Deleted Users</title>
        <meta name="description" content="View deleted user accounts and their deletion history" />
      </Helmet>
      
      <Container>
        <ContentWrapper>
          <TopBar user={currentUser} />
          
          <Breadcrumbs>
            <BreadcrumbLink onClick={() => navigate('/dashboard')}>My Account</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={() => navigate('/admin')}>Admin Dashboard</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbLink onClick={() => navigate('/admin/users')}>Users</BreadcrumbLink>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <span>Deleted Users</span>
          </Breadcrumbs>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <PageTitle>Deleted Users ({deletedUsers.length})</PageTitle>
          
          <TableContainer>
            {deletedUsers.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>🗑️</EmptyStateIcon>
                <h3>No Deleted Users</h3>
                <p>No users have been deleted from the system yet.</p>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Original ID</TableHeader>
                    <TableHeader>Deleted Date</TableHeader>
                    <TableHeader>Deleted By</TableHeader>
                    <TableHeader>Reason</TableHeader>
                    <TableHeader>Data Removed</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers.map((deletedUser) => (
                    <TableRow key={deletedUser.id}>
                      <TableCell>
                        <UserInfo>
                          <UserAvatar imageUrl={deletedUser.profilePictureURL}>
                            {!deletedUser.profilePictureURL && 
                              getInitials(deletedUser.firstName, deletedUser.lastName)
                            }
                          </UserAvatar>
                          <UserDetails>
                            <UserName>
                              {deletedUser.firstName} {deletedUser.lastName}
                            </UserName>
                            <UserEmail>{deletedUser.email}</UserEmail>
                            {deletedUser.phoneNumber && (
                              <UserEmail>{deletedUser.phoneNumber}</UserEmail>
                            )}
                          </UserDetails>
                        </UserInfo>
                      </TableCell>
                      <TableCell>
                        <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                          {deletedUser.originalUserId}
                        </code>
                      </TableCell>
                      <TableCell>{formatDate(deletedUser.deletedAt)}</TableCell>
                      <TableCell>{deletedUser.deletedByName}</TableCell>
                      <TableCell>
                        {deletedUser.deletionReason || (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No reason provided</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatsContainer>
                          {deletedUser.relatedDataDeleted && (
                            <>
                              <StatItem>Transactions: {deletedUser.relatedDataDeleted.transactions || 0}</StatItem>
                              <StatItem>Staff entries: {deletedUser.relatedDataDeleted.staffEntries || 0}</StatItem>
                              <StatItem>Gift cards: {(deletedUser.relatedDataDeleted.sentGiftCards || 0) + (deletedUser.relatedDataDeleted.receivedGiftCards || 0)}</StatItem>
                              <StatItem>Credit activities: {deletedUser.relatedDataDeleted.creditActivities || 0}</StatItem>
                              <StatItem>OTP verifications: {deletedUser.relatedDataDeleted.otpVerifications || 0}</StatItem>
                              <StatItem><strong>Total: {getTotalDeletedData(deletedUser.relatedDataDeleted)}</strong></StatItem>
                            </>
                          )}
                        </StatsContainer>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant="danger">
                          Permanently Deleted
                        </StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default DeletedUsersPage;
